import { useState, useMemo, memo } from 'react'
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  type GeographyObject,
} from 'react-simple-maps'
import { cn } from '@/lib/utils'
import { COUNTRIES } from '@/lib/countries'

// TopoJSON world map (Natural Earth 110m)
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

interface WorldMapProps {
  data: Record<string, number> // country code -> user count
  className?: string
  height?: number // Optional fixed height for the map
}

// Map country names to ISO codes (for TopoJSON which uses full names)
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'United States of America': 'US',
  'United Kingdom': 'GB',
  'United Arab Emirates': 'AE',
  'Russian Federation': 'RU',
  'Russia': 'RU',
  'Korea': 'KR',
  'South Korea': 'KR',
  'North Korea': 'KP',
  'Taiwan': 'TW',
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Syria': 'SY',
  'Syrian Arab Republic': 'SY',
  'Iran': 'IR',
  'Democratic Republic of the Congo': 'CD',
  'Republic of the Congo': 'CG',
  'Ivory Coast': 'CI',
  "Cote d'Ivoire": 'CI',
  'Tanzania': 'TZ',
  'Venezuela': 'VE',
  'Bolivia': 'BO',
  'Vietnam': 'VN',
  'Laos': 'LA',
}

// Get country code from geography properties
function getCountryCode(geo: GeographyObject): string | null {
  const { name, NAME, ISO_A2 } = geo.properties as { name?: string; NAME?: string; ISO_A2?: string }
  
  // Try ISO code first
  if (ISO_A2 && ISO_A2 !== '-99') return ISO_A2
  
  // Try name mapping
  const countryName = name || NAME || ''
  if (COUNTRY_NAME_TO_CODE[countryName]) {
    return COUNTRY_NAME_TO_CODE[countryName]
  }
  
  // Try to find in COUNTRIES list
  const match = COUNTRIES.find(c => 
    c.label.toLowerCase() === countryName.toLowerCase()
  )
  if (match) return match.value
  
  return null
}

// Get country label from code
function getCountryLabel(code: string): string {
  const country = COUNTRIES.find(c => c.value === code)
  return country?.label || code
}

// Color scale based on user count
function getColor(count: number, maxCount: number): string {
  if (count === 0) return 'rgba(255,255,255,0.02)'
  
  // Log scale for better visualization
  const intensity = Math.log(count + 1) / Math.log(maxCount + 1)
  const baseColor = { r: 99, g: 215, b: 6 } // Primary green color
  
  // Interpolate from dark to bright
  const minOpacity = 0.15
  const maxOpacity = 0.8
  const opacity = minOpacity + intensity * (maxOpacity - minOpacity)
  
  return `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, ${opacity})`
}

// Tooltip component
interface TooltipProps {
  country: string
  count: number
  position: { x: number; y: number }
}

function Tooltip({ country, count, position }: TooltipProps) {
  return (
    <div
      className="absolute z-50 pointer-events-none bg-background/95 backdrop-blur-sm border border-white/[0.1] rounded-lg px-3 py-2 shadow-xl"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%) translateY(-8px)',
      }}
    >
      <div className="text-sm font-medium text-foreground">{country}</div>
      <div className="text-xs text-muted-foreground">
        {count} user{count !== 1 ? 's' : ''}
      </div>
    </div>
  )
}

// Top countries list
interface TopCountriesProps {
  data: Record<string, number>
  limit?: number
}

function TopCountries({ data, limit = 5 }: TopCountriesProps) {
  const sorted = useMemo(() => {
    return Object.entries(data)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
  }, [data, limit])

  if (sorted.length === 0) {
    return (
      <div className="text-xs text-muted-foreground/50">
        No user location data available
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-3">
      {sorted.map(([code, count]) => (
        <div
          key={code}
          className="flex items-center gap-2 text-xs"
        >
          <span className="text-muted-foreground">{getCountryLabel(code)}</span>
          <span className="font-medium text-foreground tabular-nums bg-primary/10 text-primary px-1.5 py-0.5 rounded">
            {count}
          </span>
        </div>
      ))}
    </div>
  )
}

// Memoized geography component for performance
const MemoizedGeography = memo(function MemoizedGeography({
  geo,
  fill,
  onMouseEnter,
  onMouseLeave,
}: {
  geo: GeographyObject
  fill: string
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  return (
    <Geography
      geography={geo}
      fill={fill}
      stroke="rgba(255,255,255,0.08)"
      strokeWidth={0.5}
      style={{
        default: { outline: 'none' },
        hover: { outline: 'none', fill: 'rgba(99, 215, 6, 0.5)' },
        pressed: { outline: 'none' },
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
})

export function WorldMap({ data, className, height }: WorldMapProps) {
  const [tooltip, setTooltip] = useState<TooltipProps | null>(null)

  const maxCount = useMemo(() => {
    const counts = Object.values(data)
    return counts.length > 0 ? Math.max(...counts) : 1
  }, [data])

  const totalUsers = useMemo(() => {
    return Object.values(data).reduce((sum, count) => sum + count, 0)
  }, [data])

  const handleMouseEnter = (
    geo: GeographyObject,
    event: React.MouseEvent
  ) => {
    const code = getCountryCode(geo)
    if (!code) return

    const count = data[code] || 0
    const rect = (event.target as Element).getBoundingClientRect()
    const containerRect = (event.currentTarget as Element).closest('.world-map-container')?.getBoundingClientRect()
    
    if (containerRect) {
      setTooltip({
        country: getCountryLabel(code),
        count,
        position: {
          x: rect.left + rect.width / 2 - containerRect.left,
          y: rect.top - containerRect.top,
        },
      })
    }
  }

  const handleMouseLeave = () => {
    setTooltip(null)
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Map */}
      <div 
        className="world-map-container relative rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden"
        style={height ? { height, aspectRatio: 'auto' } : { aspectRatio: '2/1' }}
      >
        <ComposableMap
          projectionConfig={{
            scale: height ? Math.round(height * 0.35) : 147,
            center: [0, 20],
          }}
          width={height ? height * 2 : undefined}
          height={height}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup>
            <Geographies geography={GEO_URL}>
              {({ geographies }: { geographies: GeographyObject[] }) =>
                geographies.map((geo: GeographyObject) => {
                  const code = getCountryCode(geo)
                  const count = code ? data[code] || 0 : 0
                  const fill = getColor(count, maxCount)

                  return (
                    <MemoizedGeography
                      key={geo.rsmKey}
                      geo={geo}
                      fill={fill}
                      onMouseEnter={() => handleMouseEnter(geo, event as unknown as React.MouseEvent)}
                      onMouseLeave={handleMouseLeave}
                    />
                  )
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {tooltip && <Tooltip {...tooltip} />}

        {/* Legend */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[10px] text-muted-foreground/50">
          <span>0</span>
          <div className="w-20 h-2 rounded-sm bg-gradient-to-r from-white/[0.02] via-primary/30 to-primary/80" />
          <span>{maxCount}+</span>
        </div>
      </div>

      {/* Top countries */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground/50">
          {totalUsers} user{totalUsers !== 1 ? 's' : ''} across {Object.keys(data).length} countries
        </div>
        <TopCountries data={data} />
      </div>
    </div>
  )
}
